import React, { useCallback, useEffect, useState } from 'react';
import ClaimCommunicator from '../../components/claimCommunicator/claimCommunicator';
import * as actions from '../../actions';
// @ts-ignore
import { Loader } from 'arag-ui-lib';
import communicatorMessageProjections from '../../projections/communicatorMessage';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import useStateIfMounted from '../../hooks/useStateIfMounted';
import codes from '../../utils/constants/codes';
import usePagination from '../../hooks/usePagination';
import { Comments } from '../../components/claimCommunicator/claimCommunicatorComments';

type Props = {
    claimApplication: { id: string };
};
const ClaimCommunicatorContainer = (props: Props) => {
    const { claimApplication } = props;
    const dispatch = useAppDispatch();
    const fetchCommunicatorMessages = useAppSelector(
        state => state.communicatorMessage.loading,
    );
    const postCommunicatorMessageLoading = useAppSelector(
        state => state.communicatorMessage.posting,
    );
    const comments = useAppSelector(
        communicatorMessageProjections.communicatorMessageList,
    ) as Comments[];
    const pagination = useAppSelector(
        state => state.communicatorMessage.pagination,
    );
    const [progressFileDownload, setProgressFileDownload] = useStateIfMounted(
        [],
    ) as any[];
    const [isLoadingDownload, setLoadingDownload] = useState(null);

    const {
        pagerState,
        goAtPrevious,
        goAtNext,
        goAtEnd,
        goAtStart,
        goAtPage,
        setState,
    } = usePagination();

    const fetchCommunicatorHistoryAction = useCallback(
        (payload: actions.fetchCommunicatorHistoryAction) =>
            dispatch(actions.fetchCommunicatorHistory(payload)),
        [dispatch],
    );

    const createCommunicatorMessageAction = useCallback(
        (payload: actions.createCommunicatorMessageAction) =>
            dispatch(actions.createCommunicatorMessage(payload)),
        [dispatch],
    );

    useEffect(() => {
        if (pagerState.isFetching) {
            fetchCommunicatorHistoryAction({
                claimApplicationId: claimApplication.id,
                filters: {
                    page: pagerState.page,
                    limit: pagerState.limit,
                },
            });
        }
    }, [pagerState.page, pagerState.limit, pagerState.isFetching]);

    useEffect(() => {
        if (pagination) {
            setState(pagination);
        }
    }, [pagination]);

    const handleMessageSubmit = async (values: any) => {
        createCommunicatorMessageAction({
            content: values.content,
            claimApplicationId: claimApplication.id,
            attachments: Object.values(values.files)
                .filter((e: any) => e.file)
                .map((e: any) => ({ id: e.id, fileName: e.file?.name || '' })),
        });
    };

    const clickDownloadHandler = async (
        id: any,
        name: string,
        type = codes.attachment,
    ) => {
        setLoadingDownload(id);
        const blob = await GENERAL_FUNCTIONS.requestToDownloadFile(
            id,
            name,
            type,
            (id: string, progress: any) => {
                setProgressFileDownload((prevProgress: any[]) => {
                    // Find the index of the progress object with the matching fileId
                    const index = prevProgress.findIndex(
                        (item: { id: string }) => item.id === id,
                    );
                    // Create a new progress object with the updated progress value
                    const updatedProgress = { id: id, progress: progress };
                    // Update the progress array by replacing the old object with the updated one,
                    // or appending the updated object if it doesn't exist yet
                    if (index !== -1) {
                        return [
                            ...prevProgress.slice(0, index),
                            updatedProgress,
                            ...prevProgress.slice(index + 1),
                        ];
                    } else {
                        return [...prevProgress, updatedProgress];
                    }
                });
            },
        );

        if (blob && blob !== codes.warnings && blob !== codes.fileNotFound) {
            GENERAL_FUNCTIONS.downloadURI(blob, name);
            setLoadingDownload(null);
            setProgressFileDownload((prevProgress: any[]) => [
                ...prevProgress.filter(
                    (item: { id: string }) => item.id !== id,
                ),
            ]);
        }
        if (blob === codes.warnings) {
            setProgressFileDownload((prevProgress: any[]) => [
                ...prevProgress.filter(
                    (item: { id: string }) => item.id !== id,
                ),
            ]);
            setLoadingDownload(null);
        }
        if (blob === codes.fileNotFound) {
            setLoadingDownload(null);
        }
    };
    if (fetchCommunicatorMessages) {
        return <Loader />;
    }
    return (
        <ClaimCommunicator
            comments={comments}
            handleMessageSubmit={handleMessageSubmit}
            postCommunicatorMessageLoading={postCommunicatorMessageLoading}
            claimApplication={claimApplication}
            progressFileDownload={progressFileDownload}
            isLoadingDownload={isLoadingDownload}
            clickDownloadHandler={clickDownloadHandler}
            pagerState={pagerState}
            goAtPrevious={goAtPrevious}
            goAtNext={goAtNext}
            goAtStart={goAtStart}
            goAtEnd={goAtEnd}
            goAtPage={goAtPage}
        />
    );
};

export default ClaimCommunicatorContainer;
